import { Component, effect } from '@angular/core';
import { MessageService, ConfirmationService } from 'primeng/api';
import { Parcel } from '../../shared/models/parcel';
import { ParcelService } from '../../shared/services/parcel.service';
import { ImageItem } from '../../shared/models/unassigned';
import { DataService } from '../../shared/services/data.service';
import { environment as env } from '../../../environments/environment';
import { TablePageEvent } from 'primeng/table';
import { PRIMENG_MODULES } from '../../shared/primeng-imports';
import { SpinnerComponent } from "../../shared/components/spinner/spinner.component";
import { DashboardFiltersComponent } from "../dashboard-filters/dashboard-filters.component";
import { ParcelDetailComponent } from "../parcel-detail/parcel-detail.component";


@Component({
  selector: 'app-assigned-to-me',
  templateUrl: './assigned-to-me.component.html',
  styleUrl: './assigned-to-me.component.scss',
  standalone:true,
  imports: [...PRIMENG_MODULES, SpinnerComponent, DashboardFiltersComponent, ParcelDetailComponent]
})
export class AssignedToMeComponent {
  isLoading: boolean = false;
  selectedItems: ImageItem[] = [];
  users: string[] = [];
  sortField!: string;
  isSelectAllChecked: boolean = false;
  assignedToMeItems: ImageItem[] = [];
  dateRange: any;
  selectedCamera: string = ''
  selectedItemCode: string = ''
  currentUser: any;
  displayDialog: boolean = false;
  bulkUpdateLimit: number = env.bulkUpdateLimit

  cameraList = this.parcelService.getCameraList();
  itemCodes = this.parcelService.getItemCodes();

  selectedRow: Parcel[] = [];
  recordNotFound = false;
  searchParcel: any;
  filterOptions = {
    showCameraFilter: true,
    showItemCodeFilter: true,
    showDateRangeFilter: true,
    showApplyButton: true,
    showClearFilterButton: true,
    showSearchParcel: true
  }

  rowsPerPage: any;
  enableClose: boolean = true;
  undoParcelDetails: any = {};
  private timerId: any;
  readonly INITIAL_TIME = 10
  timeLeft: number = this.INITIAL_TIME;
  constructor(private parcelService: ParcelService,
    private dataService: DataService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService
  ) {
    effect(() => {
      this.currentUser = this.dataService.currentDataSignal();
    });
  }

  ngOnInit() {
    this.getAssignedToMe();
  }

  getAssignedToMe(queryString?: string) {
    this.resetTable();
    this.isLoading = true
    this.parcelService.getAssignedToUser(queryString).subscribe({
      next: (result) => {
        this.isLoading = false;
        if (result?.result?.Items) {
          this.assignedToMeItems = result.result.Items.map((item: any) => this.transformItem(item));
        } else {
          console.warn('No data received');
          this.assignedToMeItems = [];
        }
      },
      error: (err) => {
        this.isLoading = false;
        console.error('Error fetching unassigned parcels:', err);
        this.assignedToMeItems = [];
      }
    });
  }

  applySearch() {
    let queryString = '';

    if (this.selectedCamera) {
      queryString += `&captureDeviceId=${this.selectedCamera}`;
    }

    if (this.selectedItemCode) {
      queryString += `&itemCode=${this.selectedItemCode}`;
    }

    if (this.dateRange && this.dateRange.length > 0) {
      const dateFrom = this.dateRange[0]?.getTime();
      const dateTo = this.dateRange[1]?.getTime();

      if (dateFrom && dateTo) {
        queryString += `&dateFrom=${dateFrom}&dateTo=${dateTo}`;
      }
    }
    if (this.searchParcel) {
      queryString += `&itemId=${this.searchParcel}`;
    }

    this.getAssignedToMe(queryString);
  }

  reprocessParcel(event: any, row: any): void {
    event.preventDefault();
    if (this.selectedItems.length > 0) {
      return; // dont do anything if parcels are selected for bulk update. 
    }
    const queryParams = {
      itemId: row.itemId,
      userId: 'admin',
      status: 'reprocessing',
      updater: this.currentUser
    }
    this.parcelService.updateParcelAssigned(queryParams).subscribe({
      next: () => {
        this.applySearch();
        this.messageService.add({
          severity: 'success',
          summary: 'Row Submitted',
          detail: `Successfully submitted for reprocessing: ${row.itemId}`
        });
      },
      error: (err) => console.error(`Failed to update row ${row.id}`, err)
    });
  }

  onParcelClick(event: MouseEvent, parcel: any): void {
    event.preventDefault();
    this.recordNotFound = false;
    this.selectedRow = parcel;
    this.displayDialog = true;
  }

  bulkUReprocess(event: any, type: any = 'reprocessing'): void {
    event.preventDefault();
    if (this.selectedItems.length > 0) {

      let parcels = this.selectedItems.slice(0, this.bulkUpdateLimit).map(item => ({
        itemId: item.itemId,
        userId: 'admin',
        status: type,
        updater: this.currentUser
      }));

      this.isLoading = true;

      this.parcelService.bulkAssignParcel(parcels).subscribe({
        next: () => {
          let message = (this.selectedItems.length > this.bulkUpdateLimit) ? `only first ${this.bulkUpdateLimit} selected` : parcels.length.toString();
          this.messageService.add({
            severity: 'success',
            summary: 'Row Submitted',
            detail: `Successfully submitted: ${message} rows`
          });
          this.isLoading = false;
          this.applySearch();

        },
        error: (err) => {
          console.error(`Failed to bulk update`, err);
          this.isLoading = false;
        }
      });
    }
  }

  handleRecordNotFound() {
    this.recordNotFound = true;
  }

  resetModalState() {
    this.selectedRow = [];
    this.recordNotFound = false;
  }

  private resetTable(): void {
    this.selectedItems = [];
    this.assignedToMeItems = [];
  }

  onApiSuccessFromModal(data: any) {
    this.undoParcelDetails = data;
    this.applySearch();
    this.displayDialog = false;
    if (data.status === 'handled') {
      this.messageService.add({
        key: 'actionToast',
        severity: 'success',
        summary: 'Success',
        detail: "Item is Handled",
        life: 300000,
      });
      this.startCountdown();
    }
  }

  private transformItem(raw: any): ImageItem {
    return {
      captureDeviceId: raw['capturedevice-id'],
      imageNames: raw['image-names'],
      status: raw['status'],
      timestamp: raw['timestamp'],
      imageDatetimes: raw['image-datetimes'],
      itemId: raw['itemId'],
      locationId: raw['location-id'],
    };
  }

  receiveFilterApplied(params: any) {
    this.selectedCamera = params.selectedCamera;
    this.selectedItemCode = params.selectedItemCode;
    this.dateRange = params.dateRange;
    this.searchParcel = params.searchParcel;

    this.applySearch();
  }
  receiveClearFilter() {
    this.selectedCamera = '';
    this.selectedItemCode = '';
    this.dateRange = [];
    this.searchParcel = '';
    this.applySearch();
  }
  getToolTipFormattedText(type: any) {
    return this.selectedItems.length == 0 ? ' Select parcels ' : `${this.selectedItems.length} of ${this.assignedToMeItems.length} parcels selected to ${type}`
  }
  checkSelection() {
    if (this.selectedItems.length > this.bulkUpdateLimit) {
      this.enableClose = false;
      this.confirmationService.confirm({
        header: 'Limit exceeded',
        message: `You've reached the assigning limit ${this.bulkUpdateLimit}`,
        icon: 'pi pi pi-exclamation-circle text-6xl text-primary-500 mr-4',
        acceptIcon: 'pi pi-check mr-2',
        acceptLabel: 'Ok',
        acceptButtonStyleClass: 'p-button-outlined p-button-sm',
        rejectVisible: false,
        closeOnEscape: false,
        accept: () => {
          this.selectedItems = this.selectedItems.slice(0, this.bulkUpdateLimit);
        }
      });
    }
  }
  onPageChange($event: TablePageEvent) {
    let prevRowsPerPage = this.bulkUpdateLimit
    this.bulkUpdateLimit = $event.rows
    if (this.bulkUpdateLimit < prevRowsPerPage)
      this.checkSelection()
  }

  onUndo() {
    const queryParams = {
      itemId: this.undoParcelDetails.itemId,
      userId: this.currentUser,
      status: 'assigned',
      updater: this.currentUser
    }
    this.messageService.clear('actionToast');
    this.parcelService.updateParcelAssigned(queryParams).subscribe({
      next: (response) => {
        let msg = 'Item Status has been reverted successfully';
        let severity = 'success';
        let summary = 'Undo Successful'
        this.messageService.add({ severity, summary, detail: msg });
        this.closeToast();
      },
      error: (error) => {
        this.messageService.add({ severity: 'warn', summary: 'Warning', detail: 'Failed to handle item. Try again later' });
        this.closeToast();
      }
    })
    this.applySearch();
  }
  startCountdown() {
    this.timerId = setInterval(() => {
      if (this.timeLeft > 1) {
        this.timeLeft--;
      } else {
        this.closeToast();
      }
    }, 1000);
  }


  closeToast() {
    this.applySearch();
    this.clearTimer();
    this.messageService.clear('actionToast');
    
  
  }
  clearTimer() {
    clearInterval(this.timerId);
    this.timeLeft = this.INITIAL_TIME;
  }

  ngOnDestroy() {
    this.clearTimer();
  }
}
